/**
 * Orska Discovery Kit cart controls.
 *
 * The product page caps the quantity before anything reaches the cart. This is
 * the layer for everything that skips it: a cart permalink, browser dev tools,
 * a direct call to the cart API. It reads the real cart, warns when the kit
 * count is over the customer allowance, holds checkout until it is corrected,
 * and separately explains the shipping difference on a mixed cart.
 *
 * Configuration comes from window.orskaDkCartControls, published by
 * snippets/orska-dk-cart-controls.liquid.
 *
 * This is client side and therefore bypassable. Server side enforcement is the
 * order limit app's job, not this file's.
 */

(() => {
  'use strict';

  const config = window.orskaDkCartControls;
  if (!config || !config.limit || !config.handle) return;

  const NOTICE_ATTR = 'data-orska-dk-notice';
  const BLOCKED_ATTR = 'data-orska-dk-blocked';
  const CHECKOUT_SELECTOR = '#checkout, button[name="checkout"], input[name="checkout"]';

  const state = { overLimit: false, mixedCart: false };
  let applying = false;

  const cartCtas = () => Array.from(document.querySelectorAll('.cart__ctas'));

  const buildNotice = (variant, text) => {
    const notice = document.createElement('p');
    notice.className = `orska-dk-notice orska-dk-notice--${variant}`;
    notice.setAttribute(NOTICE_ATTR, variant);
    notice.setAttribute('role', variant === 'over-limit' ? 'alert' : 'status');
    notice.textContent = text;
    return notice;
  };

  const wantedVariants = () => {
    const variants = [];
    if (state.mixedCart && config.messages.mixedCart) variants.push('mixed-cart');
    if (state.overLimit && config.messages.overLimit) variants.push('over-limit');
    return variants;
  };

  // True when the DOM no longer matches the state, which happens whenever the
  // theme re-renders the cart section and drops the injected notices.
  const needsApply = () => {
    const wanted = wantedVariants();
    const present = Array.from(document.querySelectorAll(`[${NOTICE_ATTR}]`));

    if (present.length !== wanted.length * cartCtas().length) return true;

    // Notices sit immediately before the checkout actions in the order of
    // wanted, so walking backwards from the actions must meet them reversed.
    const expected = wanted.slice().reverse();
    const misplaced = cartCtas().some((ctas) => {
      let cursor = ctas;
      return expected.some((variant) => {
        const previous = cursor.previousElementSibling;
        if (!previous || previous.getAttribute(NOTICE_ATTR) !== variant) return true;
        cursor = previous;
        return false;
      });
    });
    if (misplaced) return true;

    const blockedCount = document.querySelectorAll(`[${BLOCKED_ATTR}]`).length;
    if (state.overLimit && blockedCount === 0 && document.querySelector(CHECKOUT_SELECTOR)) return true;
    if (!state.overLimit && blockedCount > 0) return true;

    return false;
  };

  const setCheckoutBlocked = (blocked) => {
    document.querySelectorAll(CHECKOUT_SELECTOR).forEach((button) => {
      if (blocked) {
        // A checkout button disabled for another reason, an empty cart for
        // instance, is left exactly as it is so it never gets re-enabled here.
        if (button.hasAttribute(BLOCKED_ATTR) || button.disabled) return;
        button.setAttribute(BLOCKED_ATTR, '');
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      } else if (button.hasAttribute(BLOCKED_ATTR)) {
        button.removeAttribute(BLOCKED_ATTR);
        button.disabled = false;
        button.removeAttribute('aria-disabled');
      }
    });
  };

  const applyState = () => {
    applying = true;

    document.querySelectorAll(`[${NOTICE_ATTR}]`).forEach((notice) => notice.remove());

    const wanted = wantedVariants();
    cartCtas().forEach((ctas) => {
      wanted.forEach((variant) => {
        const text = variant === 'over-limit' ? config.messages.overLimit : config.messages.mixedCart;
        ctas.parentElement.insertBefore(buildNotice(variant, text), ctas);
      });
    });

    setCheckoutBlocked(state.overLimit);

    // Released on a timer rather than an animation frame: a background tab can
    // withhold frames indefinitely, which would leave this stuck and silently
    // kill the observer below.
    setTimeout(() => {
      applying = false;
    }, 0);
  };

  let reading = false;
  const refresh = async () => {
    if (reading) return;
    reading = true;

    try {
      const response = await fetch(window.Theme?.routes?.cart_url ? `${window.Theme.routes.cart_url}.js` : '/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('cart unavailable');

      const cart = await response.json();
      const items = cart.items || [];
      const kits = items
        .filter((item) => item.handle === config.handle)
        .reduce((sum, item) => sum + (item.quantity || 0), 0);

      state.overLimit = kits > config.limit;
      state.mixedCart = kits > 0 && items.some((item) => item.handle !== config.handle);
    } catch (error) {
      // A cart that cannot be read must never leave checkout blocked.
      state.overLimit = false;
      state.mixedCart = false;
    } finally {
      reading = false;
    }

    applyState();
  };

  // The theme replaces the cart markup on its own schedule, so re-apply
  // whenever the DOM stops matching the state rather than guessing at timings.
  const observer = new MutationObserver(() => {
    if (applying) return;
    if (needsApply()) applyState();
  });
  observer.observe(document.body, { childList: true, subtree: true });

  document.addEventListener('cart:update', refresh);
  document.addEventListener('cart:add', refresh);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }
})();
